import { useCallback, useEffect, useMemo, useState, type ComponentProps, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'
import SidebarNav from '@components/SidebarNav'
import { getAdminNavItemsForRole, getAdminNavRoutesForRole } from '@constants/adminNav'
import { useStore } from '@store/StoreProvider'
import { toastStore } from '@store/ToastStore'
import { handleUnauthorized } from '@utils/apiHelpers'
import { useOperatorOwnPresence } from '@hooks/useOperatorOwnPresence'
import { useBillingEntitlements } from '@hooks/useBillingEntitlements'
import { PanelLeftFilledIcon } from '@components/AdminMenuIcons'
import { t } from '@store/I18nStore'

export const ADMIN_OPEN_MOBILE_SIDEBAR_EVENT = 'admin:open-mobile-sidebar'
export const ADMIN_CLOSE_MOBILE_SIDEBAR_EVENT = 'admin:close-mobile-sidebar'
export const ADMIN_TOGGLE_MOBILE_SIDEBAR_EVENT = 'admin:toggle-mobile-sidebar'
export const ADMIN_MOBILE_SIDEBAR_STATE_EVENT = 'admin:mobile-sidebar-state'

type UseAdminSidebarOptions = {
  showStatusToggle?: boolean
  onItemClick?: (item: string) => void
}

export type SidebarRenderOptions = {
  inboxCount?: number
}

type UseAdminSidebarResult = {
  navHidden: boolean
  toggleNavHidden: () => void
  openMobileSidebar: () => void
  closeMobileSidebar: () => void
  renderDesktopSidebar: (options?: SidebarRenderOptions) => ReactNode
  renderMobileSidebar: (options?: SidebarRenderOptions) => ReactNode
  isLunchMode: boolean
}

export const LS_COLLAPSED = 'chatx_sidebar_collapsed'
const LS_HIDDEN = 'chatx_sidebar_hidden'

export function useAdminSidebar(
  activeNav: string,
  options: UseAdminSidebarOptions = {},
): UseAdminSidebarResult {
  const { auth, i18n, emailVerification } = useStore()
  const { entitlements, canManageBilling } = useBillingEntitlements()
  const emailUnverified = emailVerification.blocked || auth.currentUser?.emailVerified === false
  const operatorPresence = useOperatorOwnPresence()
  const { showStatusToggle: showStatusToggleOption = true, onItemClick } = options
  const showStatusToggle = showStatusToggleOption && !emailUnverified

  const [presenceBusy, setPresenceBusy] = useState(false)
  const ownPresence = operatorPresence.presence
  const isLunchMode = operatorPresence.isOnBreak
  const presenceLoading = showStatusToggle && operatorPresence.loading && !operatorPresence.loadedOnce

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      return localStorage.getItem(LS_COLLAPSED) === '1'
    } catch {
      return false
    }
  })

  const [isNavHidden, setIsNavHidden] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      return localStorage.getItem(LS_HIDDEN) === '1'
    } catch {
      return false
    }
  })

  const [lunchConfirmOpen, setLunchConfirmOpen] = useState(false)

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  useEffect(() => {
    const onOpen = () => setIsSidebarOpen(true)
    const onClose = () => setIsSidebarOpen(false)
    const onToggle = () => setIsSidebarOpen((open) => !open)
    window.addEventListener(ADMIN_OPEN_MOBILE_SIDEBAR_EVENT, onOpen)
    window.addEventListener(ADMIN_CLOSE_MOBILE_SIDEBAR_EVENT, onClose)
    window.addEventListener(ADMIN_TOGGLE_MOBILE_SIDEBAR_EVENT, onToggle)
    return () => {
      window.removeEventListener(ADMIN_OPEN_MOBILE_SIDEBAR_EVENT, onOpen)
      window.removeEventListener(ADMIN_CLOSE_MOBILE_SIDEBAR_EVENT, onClose)
      window.removeEventListener(ADMIN_TOGGLE_MOBILE_SIDEBAR_EVENT, onToggle)
    }
  }, [])

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(ADMIN_MOBILE_SIDEBAR_STATE_EVENT, { detail: { open: isSidebarOpen } }),
    )
  }, [isSidebarOpen])

  useEffect(() => {
    try {
      localStorage.setItem(LS_COLLAPSED, isSidebarCollapsed ? '1' : '0')
    } catch {}
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.sbCollapsed = isSidebarCollapsed ? 'true' : 'false'
    }
  }, [isSidebarCollapsed])

  useEffect(() => {
    try {
      localStorage.setItem(LS_HIDDEN, isNavHidden ? '1' : '0')
    } catch {}
  }, [isNavHidden])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('lunch-mode', isLunchMode)
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('lunch-mode')
      }
    }
  }, [isLunchMode])

  useEffect(() => {
    if (!lunchConfirmOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !presenceBusy) setLunchConfirmOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lunchConfirmOpen, presenceBusy])

  const handleLunchToggle = useCallback(
    async (next: boolean) => {
      if (next) {
        setLunchConfirmOpen(true)
        return
      }
      const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')
      if (!base) {
        toastStore.show(t('errors.api_url_missing'), 'error')
        return
      }
      setPresenceBusy(true)
      try {
        const res = await fetch(`${base}/api/operator/presence/online`, {
          method: 'POST',
          headers: { ...auth.authHeader },
        })
        if (!res.ok) {
          if (handleUnauthorized(res)) return
          const text = await res.text().catch(() => '')
          toastStore.show(text.trim() || `${t('errors.chat.go_online')} (${res.status})`, 'error')
          return
        }
        const applied = await operatorPresence.applyFromFetchResponse(res, auth.currentUser?.id)
        if (!applied) {
          operatorPresence.setPresence({
            userId: auth.currentUser?.id ?? null,
            status: 'online',
            isOnBreak: false,
            breakStartedAt: null,
            breakUntil: null,
            breakReason: null,
          })
        }
      } catch {
        toastStore.show(t('errors.server_unreachable'), 'error')
      } finally {
        setPresenceBusy(false)
      }
    },
    [auth.authHeader, auth.currentUser?.id, i18n.apiBaseUrl, operatorPresence],
  )

  const confirmLunchMode = useCallback(async () => {
    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')
    if (!base) {
      toastStore.show(t('errors.api_url_missing'), 'error')
      return
    }
    setPresenceBusy(true)
    try {
      const res = await fetch(`${base}/api/operator/presence/break`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth.authHeader },
        body: JSON.stringify({ minutes: 30, reason: t('chat.status.lunch') }),
      })
      if (!res.ok) {
        if (handleUnauthorized(res)) return
        const text = await res.text().catch(() => '')
        toastStore.show(text.trim() || `${t('errors.chat.start_lunch')} (${res.status})`, 'error')
        return
      }
      const applied = await operatorPresence.applyFromFetchResponse(res, auth.currentUser?.id)
      if (!applied) {
        operatorPresence.setPresence({
          userId: auth.currentUser?.id ?? null,
          status: 'lunch',
          isOnBreak: true,
          breakStartedAt: new Date().toISOString(),
          breakUntil: null,
          breakReason: t('chat.status.lunch'),
        })
      }
      setLunchConfirmOpen(false)
    } catch {
      toastStore.show(t('errors.server_unreachable'), 'error')
    } finally {
      setPresenceBusy(false)
    }
  }, [auth.authHeader, auth.currentUser?.id, i18n.apiBaseUrl, operatorPresence])

  const cancelLunchMode = useCallback(() => {
    if (presenceBusy) return
    setLunchConfirmOpen(false)
  }, [presenceBusy])

  const lunchConfirmPortal =
    lunchConfirmOpen && typeof document !== 'undefined'
      ? createPortal(
          <LunchConfirmBackdrop onCancel={cancelLunchMode}>
            <LunchConfirmDialog
              presenceBusy={presenceBusy}
              onConfirm={() => void confirmLunchMode()}
              onCancel={cancelLunchMode}
            />
          </LunchConfirmBackdrop>,
          document.body,
        )
      : null

  const handleNavClick = useCallback(
    (item: string) => {
      onItemClick?.(item)
      setIsSidebarOpen(false)
    },
    [onItemClick],
  )

  const navItems = useMemo(
    () =>
      getAdminNavItemsForRole(auth.currentUser?.role, {
        canManageBilling: entitlements ? canManageBilling : undefined,
        emailUnverified,
      }),
    [auth.currentUser?.role, entitlements, canManageBilling, emailUnverified],
  )
  const navRoutes = useMemo(
    () =>
      getAdminNavRoutesForRole(auth.currentUser?.role, {
        canManageBilling: entitlements ? canManageBilling : undefined,
        emailUnverified,
      }),
    [auth.currentUser?.role, entitlements, canManageBilling, emailUnverified],
  )

  const sidebarProps = useMemo(
    () => ({
      items: navItems,
      active: activeNav,
      routes: navRoutes,
      onItemClick: handleNavClick,
      collapsible: true,
      collapsed: isSidebarCollapsed,
      onToggleCollapse: setIsSidebarCollapsed,
      showStatusToggle,
      isOnLunch: isLunchMode,
      onLunchToggle: handleLunchToggle,
      statusSwitchDisabled: presenceBusy || presenceLoading,
      presenceStatus: ownPresence?.status ?? null,
      breakStartedAt: ownPresence?.breakStartedAt ?? null,
      breakUntil: ownPresence?.breakUntil ?? null,
      breakReason: ownPresence?.breakReason ?? null,
    }),
    [
      activeNav,
      handleLunchToggle,
      handleNavClick,
      isSidebarCollapsed,
      isLunchMode,
      showStatusToggle,
      presenceBusy,
      presenceLoading,
      ownPresence,
      navItems,
      navRoutes,
    ],
  )

  const SidebarWithUnread = useMemo(
    () =>
      function SidebarWithUnread({
        className,
        inboxCount: inboxCountProp,
        ...props
      }: ComponentProps<typeof SidebarNav> & { inboxCount?: number }) {
        if (inboxCountProp !== undefined) {
          return <SidebarWithStoreCount className={className} {...props} inboxCountOverride={inboxCountProp} />
        }
        return <SidebarWithStoreCount className={className} {...props} />
      },
    [],
  )

  const SidebarWithStoreCount = useMemo(
    () =>
      observer(function SidebarWithStoreCount({
        className,
        inboxCountOverride,
        ...props
      }: ComponentProps<typeof SidebarNav> & { inboxCountOverride?: number }) {
        const { ui, billing } = useStore()
        const count = inboxCountOverride ?? ui.inboxDialogsCount
        const visitorsActive = ui.visitorsActiveNow
        const billingAlert = billing.billingAlert
        const navMeta = useMemo(
          () => ({
            Диалоги: {
              badge: String(count),
              unread: count > 0,
            },
            Посетители: {
              ...(visitorsActive != null ? { badge: String(visitorsActive) } : {}),
            },
            ...(billingAlert !== 'none'
              ? {
                  Биллинг: {
                    billingAlert,
                  },
                }
              : {}),
          }),
          [count, visitorsActive, billingAlert],
        )
        return <SidebarNav className={className} {...props} navMeta={navMeta} />
      }),
    [],
  )

  const renderDesktopSidebar = useCallback((options?: SidebarRenderOptions) => {
    if (isNavHidden && !lunchConfirmOpen) return null
    return (
      <>
        {!isNavHidden && (
          <SidebarWithUnread
            className="sidebar-desktop"
            {...sidebarProps}
            inboxCount={options?.inboxCount}
          />
        )}
        {lunchConfirmPortal}
      </>
    )
  }, [isNavHidden, lunchConfirmOpen, lunchConfirmPortal, sidebarProps, SidebarWithUnread])

  const renderMobileSidebar = useCallback((options?: SidebarRenderOptions) => {
    if (!isSidebarOpen) return null

    const mobileSidebar = (
      <div
        className="admin-mobile-sidebar-backdrop lg:hidden"
        role="presentation"
        onClick={() => setIsSidebarOpen(false)}
      >
        <div className="admin-mobile-sidebar-panel" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="app-header-admin-menu admin-mobile-sidebar-close"
            onClick={() => setIsSidebarOpen(false)}
            aria-label={t('nav.close_menu')}
          >
            <PanelLeftFilledIcon className="app-header-admin-menu-icon" size={22} />
          </button>
          <SidebarWithUnread
            className="admin-mobile-sidebar"
            {...sidebarProps}
            collapsed={false}
            inboxCount={options?.inboxCount}
          />
        </div>
      </div>
    )

    return typeof document !== 'undefined' ? createPortal(mobileSidebar, document.body) : mobileSidebar
  }, [isSidebarOpen, sidebarProps, SidebarWithUnread])

  const toggleNavHidden = useCallback(() => {
    setIsNavHidden((prev) => !prev)
  }, [])

  const openMobileSidebar = useCallback(() => setIsSidebarOpen(true), [])
  const closeMobileSidebar = useCallback(() => setIsSidebarOpen(false), [])

  return {
    navHidden: isNavHidden,
    toggleNavHidden,
    openMobileSidebar,
    closeMobileSidebar,
    renderDesktopSidebar,
    renderMobileSidebar,
    isLunchMode,
  }
}

function LunchConfirmBackdrop({
  children,
  onCancel,
}: {
  children: React.ReactNode
  onCancel: () => void
}) {
  return (
    <div
      className="lunch-confirm-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      {children}
    </div>
  )
}

const LunchConfirmDialog = observer(function LunchConfirmDialog({
  presenceBusy,
  onConfirm,
  onCancel,
}: {
  presenceBusy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const { i18n } = useStore()
  const t = (key: string) => i18n.t(key)
  return (
    <div
      className="lunch-confirm-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="lunch-confirm-title"
      aria-describedby="lunch-confirm-desc"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <h2 id="lunch-confirm-title" className="lunch-confirm-title">
        {t('app.warning')}!
      </h2>
      <div id="lunch-confirm-desc" className="lunch-confirm-body">
        <p>
          {t('chat.lunch.confirm_text')}
        </p>
        <p>
          {t('chat.lunch.description')}
        </p>
      </div>
      <div className="lunch-confirm-actions">
        <button
          type="button"
          className="primary-btn lunch-confirm-btn"
          onClick={onConfirm}
          disabled={presenceBusy}
        >
          {t('billing.plans.confirm_switch')}
        </button>
        <button type="button" className="ghost-btn lunch-confirm-btn" onClick={onCancel} disabled={presenceBusy}>
          {t('chat.lunch.resume_work')}
        </button>
      </div>
    </div>
  )
})

export default useAdminSidebar
